/**
 * Transcript-timing mapping utility (Stage 1 rebuild): the single pure place
 * that maps transcript positions (turns and words) to media time and back.
 *
 * It replaces the wall-clock guesswork the admin viewer papered over with
 * per-word fudge factors, gap snapping, and segment pinning. Given the persisted
 * `transcript_timings` rows (word-accurate, from Stage 2 forced alignment), the
 * legacy JSONB segments, and the conversation turns, it answers:
 *
 * - `seekTargetForWord` / `seekTargetForTurn`: where does a click go?
 * - `locate`: which turn/word is playing right now (drives the moving highlight
 *   and auto-scroll)?
 * - `visiblePlaybackSegments`: which segments are real playback surfaces?
 *
 * Two coordinate systems meet here. Word timings (`s`/`e`) and `turn_start_ms`
 * are media-relative to their home segment. A playhead is media-relative to
 * whatever segment is on screen. Both are tied to worker wall-clock through each
 * segment's anchor (`client_start_ms - clock_offset_ms` when both are present,
 * else `start_ms`), and every turn `timestamp` is worker wall-clock too, so the
 * whole timeline lives on one clock: no cross-epoch reconciliation is needed.
 *
 * When timing rows exist for a turn they are ground truth. Turns without a row
 * fall back to an utterance-level heuristic: a guest ('user') turn is timestamped
 * near its END, so its start is estimated by backing up a bounded per-word amount;
 * a hollie ('ai') turn is timestamped near its START and is used as-is. This
 * heuristic is a documented interim that Stage 2 forced alignment replaces.
 *
 * No sidecar translation: this rebuild does NOT re-home audio-hosted rows onto a
 * camera segment. A row seeks to its own declared home; the dropped audio sidecar
 * is instead neutralized for playback by {@link visiblePlaybackSegments}.
 *
 * Pure TypeScript, no DB or DOM access, no `Date.now`. Exhaustively unit-tested
 * in `./__tests__`.
 *
 * @module lib/transcript-timing
 */

import type { SegmentKind } from '../../types';
import type {
  LocateResult,
  SeekTarget,
  TimelineEntry,
  TimelineSegment,
  TimelineTurn,
  TranscriptTiming,
  TranscriptTimeline,
  TurnTiming,
} from './types';

export type {
  LocateResult,
  SeekTarget,
  TimelineEntry,
  TimelineSegment,
  TimelineTurn,
  TranscriptTiming,
  TranscriptTimingWord,
  TranscriptTimeline,
  TurnTiming,
} from './types';

/**
 * Average duration of a single spoken word in milliseconds (~150 words/min).
 * The no-row fallback uses it to back a guest ('user') utterance up from its END
 * timestamp (transcription-commit time) to an estimated START. This is the same
 * constant the old admin viewer used; it is a documented interim that Stage 2
 * forced alignment removes.
 */
export const MS_PER_SPOKEN_WORD = 400;

/**
 * Upper bound on the guest back-up, in milliseconds. A long answer would
 * otherwise estimate an implausibly early start (and could wander before the
 * segment or the previous turn); capping keeps the estimate local to the answer.
 */
export const MAX_GUEST_BACKUP_MS = 30000;

/** Maximum supported recording-segment duration, matching the call limit. */
const MAX_SEGMENT_DURATION_MS = 30 * 60 * 1000;

/**
 * The stable key that joins a segment across the JSONB prop and the timing rows:
 * `${segment_kind || 'camera'}:${segment_index}`. Rows written before
 * screen-share support default to 'camera', matching the segment default.
 */
export function segmentKey(seg: { segment_kind?: SegmentKind | null; segment_index: number }): string {
  return `${seg.segment_kind || 'camera'}:${seg.segment_index}`;
}

/**
 * The canonical turn key used everywhere: `turn.turn_id`, else
 * `'ts:' + turn.timestamp`. Renderers and persisted rows key turns the same way
 * so the utility's lookups line up with the UI. A genuine zero timestamp yields
 * 'ts:0'; a turn with neither field yields the inert 'ts:undefined' (such turns
 * carry no wall-clock, so they never enter the timeline and never get a target).
 */
export function turnKeyForTurn(turn: { turn_id?: string; timestamp?: number }): string {
  return turn.turn_id ?? `ts:${turn.timestamp}`;
}

/** Resolve a segment's kind, defaulting to 'camera' for legacy rows. */
function resolveKind(seg: TimelineSegment): SegmentKind {
  return seg.segment_kind || 'camera';
}

/**
 * Whether a status counts as a processed playback surface. `undefined` is
 * treated as processed so callers that omit status (and legacy rows) still work;
 * an explicit in-flight status (for example 'uploading') does not count.
 */
function isProcessedLike(status?: string): boolean {
  return status === undefined || status === 'processed';
}

/** Whether a speaker produces a seekable, locatable utterance (guest or hollie). */
function isSpokenSpeaker(speaker?: string): boolean {
  return speaker === 'user' || speaker === 'ai';
}

/**
 * A segment's worker-clock anchor. `client_start_ms - clock_offset_ms` is a
 * tighter anchor than the upload-biased `start_ms` when both are present
 * (`clock_offset_ms` is the worker's `client - server` estimate, so subtracting
 * it converts the client stamp to the worker clock). A `clock_offset_ms` of 0 is
 * valid, so membership is tested with `typeof`, not truthiness. When
 * `client_start_ms` is present but `clock_offset_ms` is absent the client stamp
 * cannot be reconciled and is unusable, so the anchor degrades to `start_ms`.
 */
function anchorOf(seg: TimelineSegment): number {
  if (typeof seg.client_start_ms === 'number' && typeof seg.clock_offset_ms === 'number') {
    return seg.client_start_ms - seg.clock_offset_ms;
  }
  return seg.start_ms;
}

/**
 * A segment's media duration in ms. Processed media metadata is authoritative
 * when present. Before processing, the end is measured against the same anchor
 * used for the start so correcting upload-open bias does not shorten the window.
 * Invalid or implausible values fall back to the bounded worker-clock delta.
 */
function segDuration(seg: TimelineSegment): number {
  const workerDelta = seg.end_ms - seg.start_ms;
  const workerDuration = Number.isFinite(workerDelta)
    ? Math.max(0, Math.min(workerDelta, MAX_SEGMENT_DURATION_MS))
    : 0;
  const mediaDuration = typeof seg.duration_seconds === 'number'
    ? seg.duration_seconds * 1000
    : Number.NaN;
  if (Number.isFinite(mediaDuration) && mediaDuration > 0 && mediaDuration <= MAX_SEGMENT_DURATION_MS) {
    return mediaDuration;
  }

  const anchorDuration = seg.end_ms - anchorOf(seg);
  if (Number.isFinite(anchorDuration) && anchorDuration > 0 && anchorDuration <= MAX_SEGMENT_DURATION_MS) {
    return anchorDuration;
  }
  return workerDuration;
}

/** Ordering rank per recorder kind (camera before audio before screen). */
function kindRank(seg: TimelineSegment): number {
  const kind = resolveKind(seg);
  if (kind === 'camera') return 0;
  if (kind === 'audio') return 1;
  return 2;
}

/** The home segment key for a row, or null when the row declares no home. */
function homeKeyForRow(row: TranscriptTiming): string | null {
  if (row.segment_index == null) return null;
  return `${row.segment_kind || 'camera'}:${row.segment_index}`;
}

/** Whitespace word count of a turn's trimmed text (empty text counts as 0). */
function countWords(text?: string): number {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
}

/**
 * Playback-visible segments. Audio-kind segments are hidden from playback
 * surfaces whenever any PROCESSED camera segment exists: this neutralizes stray
 * orphaned audio sidecars that pollute video-call conversations. An audio-only
 * call (no camera at all) keeps its audio segments exactly as today. Screen
 * segments are unaffected. Order is preserved and the input is not mutated.
 *
 * Scope: this applies only the audio-hide rule. Callers pass their processed
 * segment set; it does not itself filter out non-processed segments.
 */
export function visiblePlaybackSegments(segments: TimelineSegment[]): TimelineSegment[] {
  const hasProcessedCamera = segments.some(
    (s) => resolveKind(s) === 'camera' && isProcessedLike(s.status),
  );
  if (!hasProcessedCamera) return segments.slice();
  return segments.filter((s) => resolveKind(s) !== 'audio');
}

/** Enriched hosting segment: key plus worker-clock anchor and duration. */
interface PoolSegment {
  key: string;
  anchor: number;
  duration: number;
}

/** The resolved placement of a wall-clock moment onto a hosting segment. */
interface Placement {
  hostSegmentKey: string;
  offsetMs: number;
  resolvedWall: number;
  anchor: number;
}

/**
 * Place a worker-clock moment onto the hosting pool. When the moment falls
 * inside a segment window it maps directly (earliest-anchor segment wins any
 * overlap, for determinism). Otherwise it snaps to the NEAREST segment boundary
 * in wall-clock distance: before the first segment to its start, after the last
 * to its end, and inside an inter-segment gap to whichever surrounding boundary
 * is closer (ties favor the previous segment's end). It never blanket-clamps to
 * the last segment (the regression the old viewer had).
 *
 * @param wall Worker-clock moment to place.
 * @param pool Hosting segments, sorted ascending by wall-clock anchor.
 */
function placeOnPool(wall: number, pool: PoolSegment[]): Placement | null {
  if (pool.length === 0) return null;

  let contain: PoolSegment | null = null;
  for (const p of pool) {
    if (wall >= p.anchor && wall <= p.anchor + p.duration) {
      if (!contain || p.anchor < contain.anchor) contain = p;
    }
  }
  if (contain) {
    return {
      hostSegmentKey: contain.key,
      offsetMs: Math.max(0, wall - contain.anchor),
      resolvedWall: wall,
      anchor: contain.anchor,
    };
  }

  const first = pool[0];
  const last = pool[pool.length - 1];
  if (wall < first.anchor) {
    return { hostSegmentKey: first.key, offsetMs: 0, resolvedWall: first.anchor, anchor: first.anchor };
  }
  if (wall > last.anchor + last.duration) {
    const end = last.anchor + last.duration;
    return { hostSegmentKey: last.key, offsetMs: last.duration, resolvedWall: end, anchor: last.anchor };
  }
  for (let i = 0; i < pool.length - 1; i++) {
    const prev = pool[i];
    const next = pool[i + 1];
    const prevEnd = prev.anchor + prev.duration;
    if (wall > prevEnd && wall < next.anchor) {
      const distPrev = wall - prevEnd;
      const distNext = next.anchor - wall;
      if (distPrev <= distNext) {
        return { hostSegmentKey: prev.key, offsetMs: prev.duration, resolvedWall: prevEnd, anchor: prev.anchor };
      }
      return { hostSegmentKey: next.key, offsetMs: 0, resolvedWall: next.anchor, anchor: next.anchor };
    }
  }
  // Overlapping segments can leave no clean gap to land in; snap to the last end.
  const end = last.anchor + last.duration;
  return { hostSegmentKey: last.key, offsetMs: last.duration, resolvedWall: end, anchor: last.anchor };
}

/**
 * Build an aligned (row-backed) entry, or null when the row has no usable home
 * segment or start. `resolvedWallStart` and the word list are media-relative to
 * the row's own declared home: no translation onto another segment.
 */
function alignedEntry(
  key: string,
  row: TranscriptTiming,
  segByKey: Map<string, TimelineSegment>,
): TimelineEntry | null {
  const homeKey = homeKeyForRow(row);
  if (!homeKey) return null;
  const home = segByKey.get(homeKey);
  if (!home) return null;
  const words = row.words ?? [];
  const startMedia = row.turn_start_ms ?? (words.length > 0 ? words[0].s : null);
  if (startMedia == null) return null;
  const homeAnchor = anchorOf(home);
  return {
    turnKey: key,
    hostSegmentKey: homeKey,
    hostOffsetMs: Math.max(0, startMedia),
    resolvedWallStart: homeAnchor + startMedia,
    words,
    homeAnchor,
    hasRow: true,
  };
}

/**
 * Build a fallback (no-row) entry from a spoken turn's wall-clock. Guest turns
 * first resolve their END stamp to a segment, then back up by a bounded per-word
 * estimate inside that segment. The start keeps the previous turn's resolved
 * start as its monotonic floor. hollie turns are placed globally at their START
 * stamp. Returns null when the pool is empty (nowhere to host).
 *
 * @param lastResolvedWall The previous resolved turn's start (monotonic floor).
 */
function fallbackEntry(
  key: string,
  turn: TimelineTurn,
  pool: PoolSegment[],
  lastResolvedWall: number,
): TimelineEntry | null {
  if (typeof turn.timestamp !== 'number') return null;
  const endTs = turn.timestamp;

  let rawStart = endTs;
  if (turn.speaker === 'user') {
    const endPlacement = placeOnPool(endTs, pool);
    if (!endPlacement) return null;

    const wordCount = countWords(turn.text);
    if (wordCount > 0) {
      const backup = Math.min(wordCount * MS_PER_SPOKEN_WORD, MAX_GUEST_BACKUP_MS);
      rawStart = endTs - backup;
    }
    if (Number.isFinite(lastResolvedWall)) {
      rawStart = Math.max(rawStart, lastResolvedWall);
    }

    const resolvedWall = Math.min(
      Math.max(rawStart, endPlacement.anchor),
      endPlacement.resolvedWall,
    );
    return {
      turnKey: key,
      hostSegmentKey: endPlacement.hostSegmentKey,
      hostOffsetMs: resolvedWall - endPlacement.anchor,
      resolvedWallStart: resolvedWall,
      words: [],
      homeAnchor: endPlacement.anchor,
      hasRow: false,
    };
  }
  if (Number.isFinite(lastResolvedWall) && rawStart < lastResolvedWall) {
    rawStart = lastResolvedWall;
  }

  const placed = placeOnPool(rawStart, pool);
  if (!placed) return null;
  return {
    turnKey: key,
    hostSegmentKey: placed.hostSegmentKey,
    hostOffsetMs: placed.offsetMs,
    resolvedWallStart: placed.resolvedWall,
    words: [],
    homeAnchor: placed.anchor,
    hasRow: false,
  };
}

/**
 * Build a {@link TranscriptTimeline} from the legacy JSONB segments, the
 * persisted timing rows, and the conversation turns. Tolerant of malformed
 * input: unsorted or empty arrays, missing timestamps, duplicate indexes across
 * kinds, and 0-valued `clock_offset_ms` are all handled without throwing.
 *
 * @param segments Legacy JSONB-sourced segments (the viewer passes its processed set).
 * @param timingRows Persisted `transcript_timings` rows for the response.
 * @param turns The conversation turns, in display (chronological) order.
 */
export function buildTimeline(
  segments: TimelineSegment[],
  timingRows: TranscriptTiming[],
  turns: TimelineTurn[],
): TranscriptTimeline {
  const segByKey = new Map<string, TimelineSegment>();
  for (const s of segments) segByKey.set(segmentKey(s), s);

  const rowByKey = new Map<string, TranscriptTiming>();
  for (const r of timingRows) rowByKey.set(r.turn_key, r);

  const visibleSegments = visiblePlaybackSegments(segments);

  // Hosting pool: visible, non-screen segments (screen duplicates the camera
  // wall-clock window and never hosts fallback timings). Ordered by recorder
  // kind then index (per-recorder counters); the audio-hide rule already ensures
  // this is cameras for a video call and audios only for an audio-only call.
  const pool: PoolSegment[] = visibleSegments
    .filter((s) => resolveKind(s) !== 'screen')
    .slice()
    .sort((a, b) => kindRank(a) - kindRank(b) || a.segment_index - b.segment_index)
    .map((s) => ({ key: segmentKey(s), anchor: anchorOf(s), duration: segDuration(s) }));

  const entriesByKey = new Map<string, TimelineEntry>();
  const entries: TimelineEntry[] = [];
  let lastResolvedWall = Number.NEGATIVE_INFINITY;

  for (const turn of turns) {
    const key = turnKeyForTurn(turn);
    const row = rowByKey.get(key);

    // A row is ground truth when it resolves to a usable home segment.
    const aligned = row ? alignedEntry(key, row, segByKey) : null;
    if (aligned) {
      entriesByKey.set(key, aligned);
      entries.push(aligned);
      lastResolvedWall = Math.max(lastResolvedWall, aligned.resolvedWallStart);
      continue;
    }

    // Fallback: only guest/hollie turns with a wall-clock get placed. System
    // turns (and stampless turns) are skipped, so they get no seek target and do
    // not appear on the located timeline, while the spoken turns around them keep
    // a coherent monotonic order.
    if (!isSpokenSpeaker(turn.speaker)) continue;
    const fallback = fallbackEntry(key, turn, pool, lastResolvedWall);
    if (!fallback) continue;
    entriesByKey.set(key, fallback);
    entries.push(fallback);
    lastResolvedWall = Math.max(lastResolvedWall, fallback.resolvedWallStart);
  }

  const entriesByWall = entries.slice().sort((a, b) => a.resolvedWallStart - b.resolvedWallStart);

  return {
    hasAnyTimings: timingRows.length > 0,
    segByKey,
    rowByKey,
    entriesByKey,
    entriesByWall,
    visibleSegments,
  };
}

/**
 * Where clicking a specific word should seek: the word's home segment and its
 * start offset. Words in the unspoken interruption tail (index at or beyond
 * `words.length`, or negative) return null. When the turn has no word timings,
 * this falls back to {@link seekTargetForTurn}.
 */
export function seekTargetForWord(
  timeline: TranscriptTimeline,
  turnKey: string,
  wordIndex: number,
): SeekTarget | null {
  const row = timeline.rowByKey.get(turnKey);
  const words = row?.words ?? [];
  if (!row || words.length === 0) return seekTargetForTurn(timeline, turnKey);
  if (wordIndex < 0 || wordIndex >= words.length) return null;
  const homeKey = homeKeyForRow(row);
  if (!homeKey) return null;
  return { segmentKey: homeKey, offsetMs: Math.max(0, words[wordIndex].s) };
}

/**
 * Where clicking a turn should seek: the turn's resolved host segment and
 * offset. For a row-backed turn this is the row's `turn_start_ms` on its home
 * segment; for a fallback turn it is the utterance estimate snapped onto the
 * hosting pool. Returns null for turns with no placement: system turns, turns
 * without a timestamp, or any turn when there is no hosting segment.
 */
export function seekTargetForTurn(timeline: TranscriptTimeline, turnKey: string): SeekTarget | null {
  const entry = timeline.entriesByKey.get(turnKey);
  if (!entry) return null;
  return { segmentKey: entry.hostSegmentKey, offsetMs: entry.hostOffsetMs };
}

/**
 * Which turn and word are active given the currently-playing segment and the
 * playhead offset into it. Drives the moving highlight and auto-scroll.
 *
 * The playhead is converted to worker wall-clock via the playing segment's own
 * anchor, then matched against the resolved turn entries. This resolves guest
 * turns as readily as hollie turns, and resolves turns from a screen-segment
 * playhead too (screen never hosts, but its anchor still maps a playhead to
 * wall-clock). The result is the turn whose resolved start is the nearest at or
 * before the playhead, so the highlight holds steady through inter-word and
 * inter-turn pauses. `wordIndex` is the last word whose start has been reached,
 * or null at utterance granularity (no word timings, or before the first word).
 * Returns null before the first turn or when the playing segment is unknown.
 *
 * @param segmentKeyId The `segmentKey` of the segment currently playing.
 * @param offsetMs Offset into that segment's media, in ms.
 */
export function locate(
  timeline: TranscriptTimeline,
  segmentKeyId: string,
  offsetMs: number,
): LocateResult | null {
  const seg = timeline.segByKey.get(segmentKeyId);
  if (!seg) return null;
  const entries = timeline.entriesByWall;
  if (entries.length === 0) return null;

  const wall = anchorOf(seg) + offsetMs;

  // Last entry with resolvedWallStart <= wall.
  let lo = 0;
  let hi = entries.length - 1;
  let idx = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (entries[mid].resolvedWallStart <= wall) {
      idx = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (idx < 0) return null;

  const entry = entries[idx];
  let wordIndex: number | null = null;
  if (entry.words.length > 0) {
    // Last word whose start (media-relative to its home) has been reached.
    let wlo = 0;
    let whi = entry.words.length - 1;
    let widx = -1;
    while (wlo <= whi) {
      const wmid = (wlo + whi) >> 1;
      if (entry.homeAnchor + entry.words[wmid].s <= wall) {
        widx = wmid;
        wlo = wmid + 1;
      } else {
        whi = wmid - 1;
      }
    }
    wordIndex = widx < 0 ? null : widx;
  }

  return { turnKey: entry.turnKey, wordIndex };
}

/**
 * Per-turn timing summary the renderer uses to choose word-level versus
 * utterance-level UI. A turn with no row reports `timingSource: 'none'` and no
 * words.
 */
export function getTurnTiming(timeline: TranscriptTimeline, turnKey: string): TurnTiming {
  const row = timeline.rowByKey.get(turnKey);
  if (!row) {
    return { turnKey, words: [], spokenWordCount: 0, timingSource: 'none', hasWords: false };
  }
  const words = row.words ?? [];
  return {
    turnKey,
    words,
    spokenWordCount: row.spoken_word_count ?? words.length,
    timingSource: row.timing_source ?? 'none',
    hasWords: words.length > 0,
  };
}
